"use client";
import React, { useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import PersonIcon from "@mui/icons-material/PersonOutline";
import ExitIcon from "@mui/icons-material/ExitToApp";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "@portal/lib/firebase/client";
import { jwtDecode } from "jwt-decode";

interface ImpersonationData {
  email?: string;
  tenant?: string;
  adminEmail?: string;
  startedAt?: number;
  expiresAt?: number;
}

export const ImpersonationWarning: React.FC = () => {
  const [impersonationData, setImpersonationData] = useState<ImpersonationData | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const email = params.get('email');
    const tenant = params.get('tenant');
    const tokenParam = params.get('token');
    const codeParam = params.get('code');

    const storedData = sessionStorage.getItem('impersonation');

    const persist = (e?: string | null, t?: string | null) => {
      if (!e || !t) return;
      const existingRaw = sessionStorage.getItem('impersonation');
      const existing = existingRaw ? (JSON.parse(existingRaw) as ImpersonationData) : undefined;
      const startedAt = existing?.startedAt ?? Date.now();
      const data: ImpersonationData = { email: e, tenant: t, startedAt };
      setImpersonationData(data);
      sessionStorage.setItem('impersonation', JSON.stringify(data));
    };

    if (email && tenant) persist(email, tenant);

    const signInWithToken = async (tk: string) => {
      try {
        await signInWithCustomToken(auth, tk);
        const idt = await auth.currentUser?.getIdToken();
        if (idt) {
          try {
            const { exp } = jwtDecode<{ exp?: number }>(idt);
            if (exp) {
              const expiresAt = exp * 1000;
              const existingRaw = sessionStorage.getItem('impersonation');
              const existing = existingRaw ? (JSON.parse(existingRaw) as ImpersonationData) : {};
              const updated: ImpersonationData = { ...existing, expiresAt };
              setImpersonationData(updated);
              sessionStorage.setItem('impersonation', JSON.stringify(updated));
            }
          } catch (e) {
            console.warn('[impersonation] failed to decode ID token', e);
          }
        }
      } catch (e) {
        console.warn('[impersonation] failed to sign in with custom token', e);
      }
    };

    const cleanupUrl = (keys: string[]) => {
      try {
        const u = new URL(window.location.href);
        keys.forEach((k) => u.searchParams.delete(k));
        window.history.replaceState({}, document.title, u.toString());
      } catch (e) {
        console.warn('[impersonation] failed to clean URL', e);
      }
    };

    const run = async () => {
      if (tokenParam) {
        await signInWithToken(tokenParam);
        cleanupUrl(['token']);
        return;
      }
      if (codeParam) {
        try {
          const bffBase = process.env.NEXT_PUBLIC_BFF_URL || 'http://localhost:4000';
          const resp = await fetch(`${bffBase}/impersonation/exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: codeParam }),
            credentials: 'include',
          });
          if (resp.ok) {
            const { token, tenantId, email: e } = await resp.json();
            if (e && tenantId) persist(e, tenantId);
            if (token) await signInWithToken(token);
          } else {
            console.warn('[impersonation] exchange failed', await resp.text());
          }
        } catch (e) {
          console.warn('[impersonation] exchange error', e);
        }
        cleanupUrl(['code']);
      }
    };

    if (email && tenant) {
      const data: ImpersonationData = { email, tenant, startedAt: Date.now() };
      setImpersonationData(data);
      sessionStorage.setItem('impersonation', JSON.stringify(data));
    } else if (storedData) {
      const parsed = JSON.parse(storedData) as ImpersonationData;
      setImpersonationData(parsed);
    }
    void run();
  }, []);

  const [isExpired, setIsExpired] = useState(false);
  useEffect(() => {
    const tick = () => {
      const exp = impersonationData?.expiresAt;
      setIsExpired(Boolean(exp && Date.now() >= exp));
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, [impersonationData?.expiresAt]);

  const handleExitImpersonation = () => {
    sessionStorage.removeItem('impersonation');
    try {
      auth.signOut();
    } catch (e) {
      console.warn('[impersonation] signOut failed', e);
    }
    let adminUrl = process.env.NEXT_PUBLIC_ADMIN_URL;
    if (!adminUrl) {
      if (process.env.NODE_ENV === 'production') {
        try {
          const { protocol, hostname } = window.location;
          const targetHost = hostname.replace('portal.', 'admin.');
          adminUrl = `${protocol}//${targetHost}`;
        } catch {
          adminUrl = 'https://admin.greater.co';
        }
      } else {
        adminUrl = 'http://localhost:5173';
      }
    }
    const finalUrl = adminUrl || 'https://admin.greater.co';
    window.location.href = finalUrl;
  };

  if (!impersonationData) {
    return null;
  }

  return (
    <Box
      sx={(theme) => ({
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: theme.palette.warning.light,
        borderBottom: `2px solid ${theme.palette.warning.main}`,
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        zIndex: 9999,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <PersonIcon sx={{ color: '#333', fontSize: 22 }} />
        <Box>
          <Typography
            variant="body1"
            sx={(theme) => ({
              fontWeight: 600,
              color: theme.palette.warning.dark,
              lineHeight: 1.2,
            })}
          >
            Impersonation Active
          </Typography>
        </Box>
      </Box>

      <Button
        variant="contained"
        size="small"
        startIcon={<ExitIcon />}
        onClick={handleExitImpersonation}
        sx={(theme) => ({
          backgroundColor: theme.palette.warning.main,
          color: "#fff",
          "&:hover": {
            backgroundColor: theme.palette.warning.dark,
          },
          borderRadius: 1,
          textTransform: "none",
          fontWeight: 500,
        })}
      >
        {isExpired ? 'Return to Users' : 'Exit'}
      </Button>
    </Box>
  );
};

export default ImpersonationWarning;
